/**
 * Verify — sentence at a time, word highlighted as it is spoken.
 *
 * The unit here is a SENTENCE, not a word: a word out of context is often
 * unrecognisable even to the person who said it, so the sentence plays whole
 * and each word lights up as it is reached. One judgment covers the run.
 *
 *   ✗ Wrong        — the whole sentence is wrong
 *   ✓ Correct      — all of it is right
 *   ✎ Correct now  — mostly right; tap the wrong words and fix them here
 *
 * In "correct now" a tap on any word opens a picker holding Gemini's top
 * three guesses for that exact word — computed during PROCESSING, so the
 * modal opens instantly — plus the option to type your own.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageProps } from './types';
import {
  getPendingVerifySegments,
  getTaskAudio,
  listTasks,
  reconcileTask,
  setSegmentVerify,
  updateSegment,
} from '../db';
import { groupIntoSentences, wordText, type Sentence } from '../services/sentences';
import type { Segment, Task } from '../types';
import { Button, EmptyState, ProgressBar, pauseAllAudio, previewPlayer } from '../components';

export default function VerifyPage({ focusTaskId }: PageProps) {
  const [queue, setQueue] = useState<Sentence[]>([]);
  const [tasks, setTasks] = useState<Map<string, Task>>(new Map());
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [nowSec, setNowSec] = useState(0);
  const [correcting, setCorrecting] = useState(false);
  const [picker, setPicker] = useState<{ seg: Segment; index: number } | null>(null);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const current = queue[0];
  const audioUrl = current ? urls.get(current.taskId) : undefined;

  /* ---------------- queue ---------------- */

  useEffect(() => {
    let alive = true;
    void Promise.all([getPendingVerifySegments(), listTasks()]).then(([pending, ts]) => {
      if (!alive) return;
      const byTask = new Map<string, Segment[]>();
      for (const s of pending) {
        const list = byTask.get(s.taskId) ?? [];
        list.push(s);
        byTask.set(s.taskId, list);
      }
      const rank = new Map(ts.map((t, i) => [t.id, i] as const));
      const sentences: Sentence[] = [];
      // NOTE — precondition violated on paper, safe today by accident.
      //
      // `groupIntoSentences` documents that it needs the task's COMPLETE leaf
      // set: sentences are defined by where SILENCE clips fall, so a filtered
      // input drops the boundary markers and fuses sentences that should be
      // separate. `segs` here is the PENDING subset, not the complete set.
      //
      // It nevertheless produces correct sentences, for a reason nothing
      // enforces: silence clips are never given a verdict, so they never leave
      // the pending queue. Every `setSegmentVerify` call in the app writes to
      // `sentence.words`, and `groupIntoSentences` puts only non-silence rows
      // there (see its `kind === 'silence'` branch) — CorrectPage's writes come
      // from the same rows. So the silences outlive every judged word and all
      // the boundaries are still present in this filtered set.
      //
      // What would break it: giving silence clips a verdict anywhere, or a
      // queue read that filters them out. If either happens, judged sentences
      // stop separating their neighbours and this page starts serving fused
      // multi-sentence cards. Pass the complete leaf set here if you touch it.
      for (const [, segs] of byTask) sentences.push(...groupIntoSentences(segs));
      sentences.sort((a, b) => {
        if (a.taskId !== b.taskId) {
          if (focusTaskId) {
            if (a.taskId === focusTaskId) return -1;
            if (b.taskId === focusTaskId) return 1;
          }
          return (rank.get(a.taskId) ?? 0) - (rank.get(b.taskId) ?? 0);
        }
        return a.startSec - b.startSec;
      });
      setQueue(sentences);
      setTotal(sentences.length);
      setTasks(new Map(ts.map((t) => [t.id, t])));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [focusTaskId]);

  /*
   * One object URL per recording, created once and kept until the page
   * unmounts.
   *
   * The previous version listed `urls` as a dependency AND revoked in its
   * cleanup, so storing a URL re-ran the effect, whose cleanup immediately
   * revoked the URL it had just created — the player then pointed at a dead
   * blob and "Play sentence" made no sound at all. Ownership now lives in a
   * ref, and revoking happens only on unmount.
   */
  const urlsRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const tid = current?.taskId;
    if (!tid || urlsRef.current.has(tid)) return;
    let alive = true;
    void getTaskAudio(tid).then((blob) => {
      if (!alive || !blob) return;
      const url = URL.createObjectURL(blob);
      urlsRef.current.set(tid, url);
      setUrls(new Map(urlsRef.current));
    });
    return () => {
      alive = false;
    };
  }, [current?.taskId]);

  useEffect(
    () => () => {
      for (const url of urlsRef.current.values()) URL.revokeObjectURL(url);
      urlsRef.current.clear();
    },
    [],
  );

  /* ---------------- sentence playback + word highlight ---------------- */

  const spanRef = useRef<{ from: number; to: number }>({ from: 0, to: 0 });
  spanRef.current = { from: current?.startSec ?? 0, to: current?.endSec ?? 0 };

  const play = useCallback(() => {
    if (!current) return;
    if (!audioUrl) {
      setError('The audio for this recording is still loading.');
      return;
    }
    setError(null);
    const el = previewPlayer();
    if (el.src !== audioUrl) el.src = audioUrl;
    el.loop = false;
    const start = () => {
      try {
        el.currentTime = spanRef.current.from;
      } catch {
        /* pre-metadata */
      }
      void el
        .play()
        .then(() => setPlaying(true))
        .catch((e: unknown) => {
          // never fail silently again
          setError(`Could not play: ${e instanceof Error ? e.message : String(e)}`);
          setPlaying(false);
        });
    };
    if (el.readyState >= 1) start();
    else el.addEventListener('loadedmetadata', start, { once: true });
  }, [current, audioUrl]);

  const stop = useCallback(() => {
    previewPlayer().pause();
    setPlaying(false);
  }, []);

  /* follow the playhead so the spoken word can be highlighted */
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      const el = previewPlayer();
      setNowSec(el.currentTime);
      if (el.currentTime >= spanRef.current.to) {
        el.pause();
        setPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  /* new sentence: silence, reset, and play it once */
  useEffect(() => {
    pauseAllAudio();
    setPlaying(false);
    setCorrecting(false);
    setPicker(null);
    setNowSec(current?.startSec ?? 0);
  }, [current?.id]);

  useEffect(() => () => pauseAllAudio(), []);

  /*
   * The word being spoken = the LAST one that has started.
   *
   * Testing "is the playhead inside this word's span" leaves nothing lit
   * whenever the playhead lands in a gap between words — and a very short
   * word can be skipped entirely, because currentTime only advances every
   * ~16 ms. Taking the last started word means something is always
   * highlighted and nothing can be jumped over.
   */
  const activeIndex = useMemo(() => {
    if (!current || !playing) return -1;
    let idx = -1;
    for (let i = 0; i < current.words.length; i++) {
      if (current.words[i].startSec <= nowSec) idx = i;
      else break;
    }
    return idx;
  }, [current, playing, nowSec]);

  /* ---------------- verdicts ---------------- */

  const advance = useCallback(() => {
    setQueue((q) => q.slice(1));
    setDone((d) => d + 1);
  }, []);

  const judge = useCallback(
    (verdict: 'correct' | 'incorrect') => {
      if (!current || busy) return;
      setBusy(true);
      stop();
      void Promise.all(current.words.map((w) => setSegmentVerify(w.id, verdict)))
        .then(() => reconcileTask(current.taskId))
        .then(() => advance())
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
        .finally(() => setBusy(false));
    },
    [current, busy, stop, advance],
  );

  /** Accept the sentence with whatever corrections were made in place. */
  const acceptCorrected = useCallback(() => {
    if (!current || busy) return;
    setBusy(true);
    stop();
    void Promise.all(
      current.words.map((w) =>
        w.correctedText !== undefined
          ? setSegmentVerify(w.id, 'incorrect')
          : setSegmentVerify(w.id, 'correct'),
      ),
    )
      .then(() => reconcileTask(current.taskId))
      .then(() => advance())
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusy(false));
  }, [current, busy, stop, advance]);

  /* ---------------- word correction ---------------- */

  const openPicker = useCallback(
    (seg: Segment, index: number) => {
      if (!correcting) return;
      stop();
      setTyped(wordText(seg));
      setPicker({ seg, index });
    },
    [correcting, stop],
  );

  const applyWord = useCallback(
    (text: string) => {
      const p = picker;
      if (!p) return;
      const value = text.trim();
      if (!value) return;
      setBusy(true);
      void updateSegment(p.seg.id, { correctedText: value })
        .then((updated) => {
          if (!updated) return;
          setQueue((q) =>
            q.map((s) =>
              s.id !== current?.id
                ? s
                : {
                    ...s,
                    words: s.words.map((w) => (w.id === updated.id ? updated : w)),
                    text: s.words
                      .map((w) => (w.id === updated.id ? value : wordText(w)))
                      .join(' ')
                      .trim(),
                  },
            ),
          );
          setPicker(null);
        })
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
        .finally(() => setBusy(false));
    },
    [picker, current?.id],
  );

  /* ---------------- render ---------------- */

  if (loading) return null;

  if (!current) {
    return (
      <>
        <header className="page__head">
          <h1 className="page__title">Verify</h1>
        </header>
        <EmptyState icon="✅" title="Nothing to verify" hint="Process a recording on the Tasks tab." />
      </>
    );
  }

  const task = tasks.get(current.taskId);

  return (
    <>
      <header className="page__head">
        <h1 className="page__title">Verify</h1>
        <span className="page__sub">{queue.length} left</span>
      </header>

      <ProgressBar value={done} max={total || 1} showCount />
      {task && <p className="mono vf-task">{task.name}</p>}

      {/* the sentence, word by word */}
      <section className="card vf-card">
        <p className="vf-sentence" dir="auto">
          {current.words.map((w, i) => {
            const text = wordText(w);
            const isActive = i === activeIndex;
            const edited = w.correctedText !== undefined;
            return (
              <button
                key={w.id}
                type="button"
                className={`vf-word${isActive ? ' is-active' : ''}${edited ? ' is-edited' : ''}${
                  correcting ? ' is-tappable' : ''
                } vf-word--${w.kind}`}
                onClick={() => openPicker(w, i)}
                disabled={!correcting || busy}
              >
                {text}
              </button>
            );
          })}
        </p>
        <p className="mono vf-meta">
          {current.words.length} word{current.words.length === 1 ? '' : 's'} ·{' '}
          {(current.endSec - current.startSec).toFixed(2)}s
        </p>
      </section>

      {error ? <p className="vp-note">{error}</p> : null}

      <div className="row vf-play">
        <Button block icon={playing ? '⏸' : '▶'} onClick={playing ? stop : play} disabled={busy}>
          {playing ? 'Stop' : 'Play sentence'}
        </Button>
      </div>

      {correcting ? (
        <div className="col vf-actions">
          <p className="vf-hint">Tap any word to fix it, then keep going.</p>
          <Button block variant="primary" size="lg" onClick={acceptCorrected} disabled={busy}>
            ✓ Done correcting
          </Button>
          <Button block onClick={() => setCorrecting(false)} disabled={busy}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="col vf-actions">
          <div className="row vf-verdicts">
            <Button block className="vf-wrong" size="lg" onClick={() => judge('incorrect')} disabled={busy}>
              ✗ Wrong
            </Button>
            <Button block variant="primary" size="lg" onClick={() => judge('correct')} disabled={busy}>
              ✓ Correct
            </Button>
          </div>
          <Button block onClick={() => setCorrecting(true)} disabled={busy}>
            ✎ Correct now
          </Button>
        </div>
      )}

      {/* word picker: options precomputed during processing */}
      {picker && (
        <div className="ce" role="dialog" aria-modal="true">
          <div className="ce__sheet">
            <div className="row ce__head">
              <strong dir="auto">{wordText(picker.seg)}</strong>
              <span className="spacer" />
              <span className="mono ce__len">{Math.round(picker.seg.confidence * 100)}%</span>
            </div>
            <p className="mono vf-trace">
              {picker.seg.name ? `${picker.seg.name} · ` : ''}
              {Math.round((picker.seg.endSec - picker.seg.startSec) * 1000)}ms ·{' '}
              {picker.seg.startSec.toFixed(3)}–{picker.seg.endSec.toFixed(3)}
              {picker.seg.trace?.length ? ` · made by: ${picker.seg.trace.join(' → ')}` : ''}
            </p>

            {picker.seg.alternatives && picker.seg.alternatives.length > 0 ? (
              <div className="col vf-alts">
                {picker.seg.alternatives.map((a) => (
                  <Button key={a.text} block dir="auto" onClick={() => applyWord(a.text)} disabled={busy}>
                    {a.text}
                    <span className="vf-alt__conf mono">{Math.round(a.confidence * 100)}%</span>
                  </Button>
                ))}
              </div>
            ) : (
              <p className="vf-hint">No suggestions were prepared for this word — type it below.</p>
            )}

            <input
              className="vf-input"
              dir="auto"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="type the word"
              autoFocus
            />
            <div className="row ce__actions">
              <Button block onClick={() => setPicker(null)} disabled={busy}>
                Cancel
              </Button>
              <Button block variant="primary" onClick={() => applyWord(typed)} disabled={busy || !typed.trim()}>
                Use this
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
